/**
 * Cliente PartyKit compartilhado: socket authed, handlers e eventos DOM.
 *
 * Host → partykitHost | Circuit → partykitCircuit | HTTP → partykitNotify
 * Re-exporta a API pública usada pelos hooks (import path estável).
 */
import PartySocket from 'partysocket';
import { getValidAccessToken } from '../../../lib/supabase';
import { PARTYKIT_HOST } from './partykitHost';
import {
    canUsePartykit,
    reportPartykitFailure,
    reportPartykitSuccess,
    type PartyKind,
} from './partykitCircuit';

export {
    CANONICAL_PARTYKIT_HOST,
    resolvePartykitHost,
    isValidPartykitHost,
    PARTYKIT_CONNECTION_DEBOUNCE_MS,
    PARTYKIT_CHAT_DEBOUNCE_MS,
    PARTYKIT_MESSAGE_REFRESH_MS,
    PARTYKIT_FETCH_THROTTLE_MS,
    PARTYKIT_LIST_REFRESH_MS,
} from './partykitHost';

export {
    canUsePartykit,
    createPartykitCooldownController,
} from './partykitCircuit';

export {
    postPartykitOrgEvent,
    notifyPartykitLocalMessage,
} from './partykitNotify';

/** Uma tentativa por socket — o circuit breaker + cooldown recriam a conexão. */
const PARTYKIT_MAX_RETRIES = 1;

type PartySocketInternals = {
    _ws?: WebSocket;
    _shouldReconnect?: boolean;
    _closeCalled?: boolean;
    _connectTimeout?: ReturnType<typeof setTimeout>;
    _uptimeTimeout?: ReturnType<typeof setTimeout>;
};

const internals = (socket: PartySocket) => socket as unknown as PartySocketInternals;

export function emitConversationMetaChanged(detail: {
    organizationId: string;
    phone?: string;
    remoteJid?: string;
    connectionId?: string;
    status?: 'closed' | 'active';
    unreadCount?: number;
    source?: string;
}): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('conversation:meta_changed', { detail }));
}

/** JWT de usuário; null se sessão inválida (não inicia handshake). */
async function resolvePartyAccessToken(): Promise<string | null> {
    try {
        const token = await getValidAccessToken();
        return token || null;
    } catch {
        return null;
    }
}

function createPartySocket(
    room: string,
    party: PartyKind,
    query?: Record<string, string>,
    /** Token já validado — 1ª URL sync; reconnects renovam via getValidAccessToken. */
    initialToken?: string,
): PartySocket {
    let usedInitial = !initialToken;
    return new PartySocket({
        host: PARTYKIT_HOST,
        room,
        party,
        query: async () => {
            if (!usedInitial && initialToken) {
                usedInitial = true;
                return { ...query, token: initialToken };
            }
            const token = await resolvePartyAccessToken();
            if (!token) throw new Error('partykit: unauthenticated');
            return { ...query, token };
        },
        maxRetries: PARTYKIT_MAX_RETRIES,
        startClosed: true,
    });
}

export function connectPartySocket(socket: PartySocket): void {
    socket.reconnect();
}

/**
 * Cria PartySocket só com sessão válida (ainda startClosed).
 * Preferir claimAuthedPartySocket nos effects (guarda de corrida).
 */
async function createAuthedPartySocket(
    room: string,
    party: PartyKind,
    query?: Record<string, string>,
): Promise<PartySocket | null> {
    if (!canUsePartykit(party)) return null;
    const token = await resolvePartyAccessToken();
    if (!token) return null;
    return createPartySocket(room, party, query, token);
}

/**
 * createAuthed + guarda de corrida: se o effect cancelou ou outra tentativa
 * mais nova ganhou, fecha o socket e devolve null (evita WS órfão).
 */
export async function claimAuthedPartySocket(
    room: string,
    party: PartyKind,
    options: {
        query?: Record<string, string>;
        isCancelled: () => boolean;
        attempt: number;
        latestAttempt: () => number;
    },
): Promise<PartySocket | null> {
    const socket = await createAuthedPartySocket(room, party, options.query);
    if (options.isCancelled() || options.attempt !== options.latestAttempt() || !socket) {
        safeClosePartySocket(socket);
        return null;
    }
    return socket;
}

type PartyAssignmentFields = {
    phone?: string;
    assignedTo?: string | null;
    organizationId?: string;
    status?: string;
    unreadCount?: number;
    remoteJid?: string;
    connectionId?: string;
};

/** Única fonte do broadcast assignment:changed (sync + status). */
export function emitPartyAssignmentChanged(data: PartyAssignmentFields): boolean {
    if (typeof window === 'undefined') return false;
    const hasAssignment = Object.prototype.hasOwnProperty.call(data, 'assignedTo');
    if (
        data.phone === undefined
        && data.status === undefined
        && data.unreadCount === undefined
        && !hasAssignment
    ) {
        return false;
    }
    window.dispatchEvent(new CustomEvent('assignment:changed', {
        detail: {
            phone: data.phone,
            organizationId: data.organizationId,
            status: data.status,
            unreadCount: data.unreadCount,
            remoteJid: data.remoteJid,
            connectionId: data.connectionId,
            source: 'partykit',
            ...(hasAssignment ? { userId: data.assignedTo ?? null } : {}),
        },
    }));
    return true;
}

/** Lifecycle WS compartilhado pelos hooks chat/status/sync (DRY). */
export function attachPartySocketHandlers(
    socket: PartySocket,
    party: PartyKind,
    isCancelled: () => boolean,
    handlers: {
        onOpen?: () => void;
        onMessage?: (raw: string) => void;
        onClose?: () => void;
        armCooldown?: () => void;
    },
): void {
    socket.addEventListener('open', () => {
        if (isCancelled()) {
            safeClosePartySocket(socket);
            return;
        }
        reportPartykitSuccess(party);
        handlers.onOpen?.();
    });
    socket.addEventListener('message', (event) => {
        if (isCancelled()) return;
        handlers.onMessage?.(String(event.data));
    });
    socket.addEventListener('close', () => {
        if (!isCancelled()) handlers.onClose?.();
    });
    socket.addEventListener('error', () => {
        if (isCancelled()) return;
        reportPartykitFailure(party);
        handlers.armCooldown?.();
        handlers.onClose?.();
    });
}

function isPartySocketClosed(socket: PartySocket | null): boolean {
    if (!socket) return true;
    const readyState = internals(socket)._ws?.readyState ?? socket.readyState;
    return readyState === WebSocket.CLOSED || readyState === WebSocket.CLOSING;
}

export function attachPartykitVisibilityReconnect(
    party: PartyKind,
    getSocket: () => PartySocket | null,
    reconnect: () => void,
    isCancelled: () => boolean,
): () => void {
    const handler = () => {
        if (document.visibilityState !== 'visible' || isCancelled()) return;
        if (!canUsePartykit(party) || !isPartySocketClosed(getSocket())) return;
        reconnect();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
}

export function safeClosePartySocket(socket: PartySocket | null): void {
    if (!socket) return;
    try {
        const internal = internals(socket);
        // Impede auto-retry do PartySocket após teardown (evita flood no console).
        internal._shouldReconnect = false;
        internal._closeCalled = true;
        clearTimeout(internal._connectTimeout);
        clearTimeout(internal._uptimeTimeout);
        const ws = internal._ws;
        if (!ws) {
            // query async ainda sem _ws — _closeCalled aborta o _connect (sem warning).
            return;
        }
        if (ws.readyState === WebSocket.CONNECTING) {
            // Nunca chamar close() em CONNECTING (Chrome + partysocket ws.js:374).
            ws.addEventListener('open', () => {
                try {
                    if (ws.readyState === WebSocket.OPEN) ws.close(1000, 'cancelled');
                } catch { /* ignore */ }
            }, { once: true });
            return;
        }
        if (ws.readyState !== WebSocket.CLOSED) {
            ws.close(1000, 'cancelled');
        }
    } catch {
        /* ignore */
    }
}
